import React, { useEffect, useState } from "react";
import { Toaster, toast } from "sonner";
import { Truck, ArrowUp, ArrowDown, Save, Eye, EyeOff, Layers, Activity, AlertCircle } from "lucide-react";
import DashboardLayout from "../components/DashboardLayout";
import api from "../lib/api";

export default function AdminShippingProviders() {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showInternal, setShowInternal] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/shipping-providers");
      setProviders(data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load providers");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Auto-refresh diagnostics every 30s while page is open
  useEffect(() => {
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  const update = async (code, patch) => {
    setSaving(true);
    try {
      const { data } = await api.put(`/admin/shipping-providers/${code}`, patch);
      setProviders(data.providers || []);
      toast.success("Saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const move = (code, dir) => {
    const idx = providers.findIndex((p) => p.code === code);
    if (idx < 0) return;
    const target = dir === "up" ? idx - 1 : idx + 1;
    if (target < 0 || target >= providers.length) return;
    const me = providers[idx]; const other = providers[target];
    update(me.code, { priority: other.priority });
    update(other.code, { priority: me.priority });
  };

  const aggregatorChoices = providers.filter((p) => p.kind === "aggregator");

  if (loading) return <DashboardLayout><div className="text-sm text-zinc-500">Loading…</div></DashboardLayout>;

  return (
    <DashboardLayout>
      <Toaster position="top-right" richColors />
      <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">Admin</div>
          <h1 data-testid="shipping-providers-title" className="font-display text-3xl font-black tracking-tighter">Shipping providers</h1>
          <p className="text-sm text-zinc-600 mt-1 max-w-2xl">
            Configure your carrier aggregator slots. End users see only the public label
            (Ship&nbsp;1 / Ship&nbsp;2 / …) — vendor names stay private. Reorder priority,
            disable any slot during outages, or compose <b>Ship 4</b> from a combination of
            other slots (cheapest / fastest / all merged).
          </p>
        </div>
        <button data-testid="toggle-internal-codes" onClick={() => setShowInternal((v) => !v)}
          className="inline-flex items-center gap-1 text-sm text-zinc-600 hover:text-zinc-900 border border-zinc-300 rounded-sm px-3 py-1.5">
          {showInternal ? <EyeOff size={14} /> : <Eye size={14} />}
          {showInternal ? "Hide" : "Show"} internal codes
        </button>
      </div>

      <div className="space-y-3">
        {providers.map((p, i) => (
          <ProviderRow
            key={p.code}
            p={p}
            showInternal={showInternal}
            saving={saving}
            canMoveUp={i > 0}
            canMoveDown={i < providers.length - 1}
            aggregatorChoices={aggregatorChoices}
            onMove={(dir) => move(p.code, dir)}
            onSave={(patch) => update(p.code, patch)}
          />
        ))}
      </div>

      <div className="mt-10 bg-zinc-50 border border-zinc-200 rounded-md p-5 text-sm text-zinc-700">
        <div className="font-display font-bold mb-2">How it works</div>
        <ul className="space-y-1.5 text-zinc-600 list-disc pl-5">
          <li>Rate-shopping queries every <b>enabled</b> aggregator slot in priority order and merges results.</li>
          <li><b>Ship 4 (combination)</b> re-uses rates from selected child slots — pick <i>cheapest</i> or <i>fastest</i> for one optimal option, or <i>all</i> to surface every option re-tagged with the Ship 4 label.</li>
          <li>End users see only the <b>label</b> on rates; the actual vendor name is never sent to the client.</li>
          <li>To enable a slot, set the corresponding env keys (EASYPOST_API_KEY / WEBSHIP_* / EASYSHIP_API_KEY) and restart.</li>
          <li>Diagnostics auto-refresh every 30 seconds. Last 50 quote attempts per slot.</li>
        </ul>
      </div>
    </DashboardLayout>
  );
}

function ProviderRow({ p, showInternal, saving, canMoveUp, canMoveDown, aggregatorChoices, onMove, onSave }) {
  const [label, setLabel] = useState(p.label);
  const [dirty, setDirty] = useState(false);
  const [showCombo, setShowCombo] = useState(false);
  useEffect(() => { setLabel(p.label); setDirty(false); }, [p.label]);

  const statusColor = p.status === "live"
    ? "bg-emerald-50 border-emerald-300 text-emerald-700"
    : "bg-zinc-100 border-zinc-300 text-zinc-500";

  const isCombination = p.kind === "combination";
  const d = p.diagnostics || {};
  const successPct = d.success_rate != null ? Math.round(d.success_rate * 100) : null;
  const successColor = successPct == null ? "text-zinc-400"
    : successPct >= 95 ? "text-emerald-600"
    : successPct >= 70 ? "text-amber-600" : "text-red-600";

  const toggleChild = (code) => {
    const current = new Set(p.combination?.children || []);
    if (current.has(code)) current.delete(code); else current.add(code);
    onSave({ combination_children: [...current] });
  };

  return (
    <div data-testid={`shipping-provider-row-${p.code}`} className={`bg-white border ${isCombination ? "border-blue-300" : "border-zinc-200"} rounded-md p-5`}>
      <div className="flex flex-col md:flex-row md:items-center gap-4">
        <div className="flex flex-col items-center w-12 shrink-0">
          <button data-testid={`reorder-up-${p.code}`} onClick={() => onMove("up")} disabled={!canMoveUp || saving}
            className="text-zinc-400 hover:text-zinc-900 disabled:opacity-30"><ArrowUp size={16} /></button>
          <div className="font-mono-plex text-lg font-bold">{p.priority}</div>
          <button data-testid={`reorder-down-${p.code}`} onClick={() => onMove("down")} disabled={!canMoveDown || saving}
            className="text-zinc-400 hover:text-zinc-900 disabled:opacity-30"><ArrowDown size={16} /></button>
        </div>

        <div className={`${isCombination ? "bg-blue-600" : "bg-zinc-900"} text-white rounded-sm w-12 h-12 flex items-center justify-center shrink-0`}>
          {isCombination ? <Layers size={20} /> : <Truck size={20} />}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <input data-testid={`label-input-${p.code}`} value={label}
              onChange={(e) => { setLabel(e.target.value); setDirty(true); }}
              className="font-display font-bold text-lg bg-transparent border-b border-transparent focus:border-zinc-300 outline-none px-1" />
            <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm border ${statusColor}`}>
              {p.status === "live" ? "Live" : "Not configured"}
            </span>
            {isCombination && <span className="text-[10px] uppercase tracking-wider bg-blue-100 text-blue-700 border border-blue-300 px-2 py-0.5 rounded-sm">Combination</span>}
            {showInternal && <code className="text-[11px] font-mono-plex bg-zinc-100 text-zinc-600 px-1.5 py-0.5 rounded">{p.code}</code>}
          </div>
          <div className="text-xs text-zinc-500 mt-0.5">
            {isCombination
              ? `Composes rates from selected child slots. ${p.combination?.children?.length || 0} child${(p.combination?.children?.length || 0) === 1 ? "" : "ren"} selected · mode: ${p.combination?.mode || "cheapest"}`
              : (p.status === "live" ? "Aggregator credentials detected — ready to quote and book."
                                     : "Add credentials in backend .env (or Emergent secrets vault) to activate.")}
          </div>
        </div>

        {/* Diagnostics */}
        {!isCombination && (
          <div data-testid={`diagnostics-${p.code}`} className="text-xs flex items-center gap-3 text-zinc-600 shrink-0">
            <div className="flex flex-col items-end">
              <div className="inline-flex items-center gap-1 font-mono-plex">
                <Activity size={12} /> <span className={successColor}>{successPct == null ? "—" : `${successPct}%`}</span>
              </div>
              <div className="text-[10px] text-zinc-400">{d.sample_count || 0} samples</div>
            </div>
            <div className="text-right">
              <div className="font-mono-plex">{d.avg_latency_ms ? `${d.avg_latency_ms}ms` : "—"}</div>
              <div className="text-[10px] text-zinc-400">avg latency</div>
            </div>
          </div>
        )}

        <label className="inline-flex items-center gap-2 text-sm shrink-0">
          <input data-testid={`enabled-toggle-${p.code}`} type="checkbox"
            checked={p.enabled} disabled={!p.configured || saving}
            onChange={(e) => onSave({ enabled: e.target.checked })}
            className="w-4 h-4 cursor-pointer disabled:cursor-not-allowed" />
          <span className={p.enabled ? "text-zinc-900" : "text-zinc-400"}>Enabled</span>
        </label>

        {dirty && (
          <button data-testid={`save-label-${p.code}`} onClick={() => onSave({ label })}
            disabled={saving} className="brand-bg text-white rounded-sm px-3 py-1.5 text-sm inline-flex items-center gap-1 disabled:opacity-50">
            <Save size={14} /> Save
          </button>
        )}

        {isCombination && (
          <button data-testid={`toggle-combo-${p.code}`} onClick={() => setShowCombo((v) => !v)}
            className="text-xs border border-blue-300 text-blue-700 px-3 py-1.5 rounded-sm hover:bg-blue-50">
            {showCombo ? "Hide" : "Configure"} mix
          </button>
        )}
      </div>

      {/* Last error banner */}
      {!isCombination && d.last_error && (
        <div className="mt-3 text-xs text-red-700 bg-red-50 border border-red-200 rounded-sm px-3 py-2 inline-flex items-start gap-2">
          <AlertCircle size={12} className="mt-0.5 shrink-0" />
          <span><b>Last error:</b> {d.last_error}</span>
        </div>
      )}

      {/* Per-program billing panel — admin sets the platform surcharge here */}
      {!isCombination && (
        <BillingPanel p={p} saving={saving} onSave={onSave} />
      )}

      {/* Combination configurator */}
      {isCombination && showCombo && (
        <div className="mt-5 pt-5 border-t border-zinc-200">
          <div className="text-xs uppercase tracking-wider text-zinc-500 mb-3">Selection mode</div>
          <div className="flex gap-2 flex-wrap mb-5">
            {[
              { v: "cheapest", l: "Cheapest from any child" },
              { v: "fastest", l: "Fastest from any child" },
              { v: "all", l: "All rates merged" },
            ].map((m) => (
              <button key={m.v} data-testid={`combo-mode-${m.v}`}
                onClick={() => onSave({ combination_mode: m.v })}
                className={`text-xs rounded-sm px-3 py-1.5 border ${
                  (p.combination?.mode || "cheapest") === m.v
                    ? "bg-blue-600 text-white border-blue-600"
                    : "bg-white text-zinc-700 border-zinc-300 hover:bg-zinc-50"
                }`}>
                {m.l}
              </button>
            ))}
          </div>

          <div className="text-xs uppercase tracking-wider text-zinc-500 mb-3">Child slots to include</div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {aggregatorChoices.map((c) => {
              const selected = (p.combination?.children || []).includes(c.code);
              const usable = c.configured;
              return (
                <button
                  key={c.code}
                  data-testid={`combo-child-${c.code}`}
                  disabled={!usable}
                  onClick={() => toggleChild(c.code)}
                  className={`text-left text-sm border rounded-sm p-3 transition-all ${
                    selected ? "bg-blue-50 border-blue-400" : "bg-white border-zinc-200 hover:border-zinc-400"
                  } ${!usable ? "opacity-40 cursor-not-allowed" : "cursor-pointer"}`}
                >
                  <div className="font-display font-bold">{c.label}</div>
                  <div className="text-[11px] text-zinc-500 mt-0.5">{c.status === "live" ? "Live" : "Not configured"}</div>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function BillingPanel({ p, saving, onSave }) {
  const b = p.billing || {};
  const [pct, setPct] = useState(String(b.markup_percent ?? 10));
  const [minv, setMinv] = useState(String(b.markup_min ?? 0.5));
  const [flat, setFlat] = useState(String(b.markup_flat ?? 0));
  const [mode, setMode] = useState(b.billing_mode || "wallet");
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setPct(String(b.markup_percent ?? 10));
    setMinv(String(b.markup_min ?? 0.5));
    setFlat(String(b.markup_flat ?? 0));
    setMode(b.billing_mode || "wallet");
    setDirty(false);
  }, [b.markup_percent, b.markup_min, b.markup_flat, b.billing_mode]);

  const save = () => {
    onSave({
      markup_percent: parseFloat(pct) || 0,
      markup_min: parseFloat(minv) || 0,
      markup_flat: parseFloat(flat) || 0,
      billing_mode: mode,
    });
  };

  // Preview surcharge on a $20 carrier rate so admin sees the effect immediately
  const sampleCarrier = 20;
  const computed = Math.max(
    (parseFloat(pct) || 0) / 100 * sampleCarrier + (parseFloat(flat) || 0),
    parseFloat(minv) || 0
  );

  return (
    <div data-testid={`billing-panel-${p.code}`} className="mt-4 pt-4 border-t border-zinc-200">
      <div className="text-xs uppercase tracking-wider text-zinc-500 mb-3 flex items-center gap-2">
        Platform billing surcharge
        <span className="text-[10px] normal-case tracking-normal text-zinc-400">— charged on every shipment booked through this slot</span>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 items-end">
        <label className="block">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Markup %</div>
          <input data-testid={`markup-pct-${p.code}`} type="number" step="0.1" min="0" max="100"
            value={pct} onChange={(e) => { setPct(e.target.value); setDirty(true); }}
            className="w-full text-sm border border-zinc-300 rounded-sm px-2 py-1.5 font-mono-plex" />
        </label>
        <label className="block">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Min ($)</div>
          <input data-testid={`markup-min-${p.code}`} type="number" step="0.01" min="0"
            value={minv} onChange={(e) => { setMinv(e.target.value); setDirty(true); }}
            className="w-full text-sm border border-zinc-300 rounded-sm px-2 py-1.5 font-mono-plex" />
        </label>
        <label className="block">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Flat add-on ($)</div>
          <input data-testid={`markup-flat-${p.code}`} type="number" step="0.01" min="0"
            value={flat} onChange={(e) => { setFlat(e.target.value); setDirty(true); }}
            className="w-full text-sm border border-zinc-300 rounded-sm px-2 py-1.5 font-mono-plex" />
        </label>
        <label className="block">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Billed via</div>
          <select data-testid={`billing-mode-${p.code}`} value={mode}
            onChange={(e) => { setMode(e.target.value); setDirty(true); }}
            className="w-full text-sm border border-zinc-300 rounded-sm px-2 py-1.5 bg-white">
            <option value="wallet">Wallet (full)</option>
            <option value="saved_card">Saved card (carrier) + wallet (surcharge)</option>
            <option value="carrier_account">User's carrier account + wallet (surcharge)</option>
          </select>
        </label>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Preview on $20 rate</div>
          <div className="text-sm font-mono-plex font-bold text-zinc-900">+${computed.toFixed(2)}</div>
        </div>
      </div>
      {dirty && (
        <div className="mt-3">
          <button data-testid={`save-billing-${p.code}`} onClick={save} disabled={saving}
            className="brand-bg text-white rounded-sm px-3 py-1.5 text-sm inline-flex items-center gap-1 disabled:opacity-50">
            <Save size={14} /> Save billing
          </button>
        </div>
      )}
    </div>
  );
}
